import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchArchivedProjects,
  permanentlyDeleteArchivedProjects,
  restoreArchivedProject,
} from "./archivedProjectsApi";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function ArchivedProjectsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchArchivedProjects({ limit: 500 });
      setRows(data);
      setSelectedIds((prev) => {
        const next = new Set();
        const valid = new Set(data.map((r) => r.id));
        for (const id of prev) {
          if (valid.has(id)) next.add(id);
        }
        return next;
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not load archived projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedCount = selectedIds.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (rows.length === 0) return prev;
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }, [rows]);

  const selectedList = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds]
  );

  async function onRestoreSelected() {
    if (selectedList.length === 0) return;
    const label =
      selectedList.length === 1
        ? `project ${selectedList[0].work_order || selectedList[0].name || selectedList[0].id}`
        : `${selectedList.length} projects`;
    if (!window.confirm(`Restore ${label} to the live board?`)) return;

    setBusy(true);
    setError("");
    setStatus("");
    let restored = 0;
    const failures = [];
    for (const row of selectedList) {
      try {
        await restoreArchivedProject(row.id);
        restored += 1;
      } catch (err) {
        failures.push(
          err?.response?.data?.error ||
            `${row.work_order || row.name || row.id}: ${err?.message || "restore failed"}`
        );
      }
    }
    setBusy(false);
    if (failures.length === 0) {
      setStatus(`Restored ${restored} project${restored === 1 ? "" : "s"}.`);
    } else {
      setError(
        `Restored ${restored}; ${failures.length} failed. First: ${failures[0]}`
      );
    }
    setSelectedIds(new Set());
    await load();
  }

  async function onPermanentDeleteSelected() {
    if (selectedList.length === 0) return;
    const label =
      selectedList.length === 1
        ? `archive entry ${selectedList[0].work_order || selectedList[0].name || selectedList[0].id}`
        : `${selectedList.length} archive entries`;
    if (
      !window.confirm(
        `Permanently delete ${label}? This cannot be undone and the snapshot will be lost.`
      )
    ) {
      return;
    }

    setBusy(true);
    setError("");
    setStatus("");
    try {
      const result = await permanentlyDeleteArchivedProjects(selectedList.map((r) => r.id));
      const n = result?.deleted ?? 0;
      setStatus(`Permanently deleted ${n} archive entr${n === 1 ? "y" : "ies"}.`);
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Permanent delete failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="plugins-card plugins-card-wide" style={{ padding: "1rem 1.25rem" }}>
      <h3 className="plugins-card-name" style={{ marginTop: 0 }}>
        Archived projects
      </h3>
      <p className="plugins-card-desc">
        Projects removed from the board are kept here as snapshots. Restore them to the live board,
        or permanently delete entries you no longer need.
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <button
          type="button"
          className="email-triggers-button email-triggers-button-primary"
          disabled={busy || selectedCount === 0}
          onClick={onRestoreSelected}
        >
          Restore selected{selectedCount > 0 ? ` (${selectedCount})` : ""}
        </button>
        <button
          type="button"
          className="email-triggers-button"
          disabled={busy || selectedCount === 0}
          onClick={onPermanentDeleteSelected}
        >
          Permanently delete{selectedCount > 0 ? ` (${selectedCount})` : ""}
        </button>
        <button type="button" className="email-triggers-button" disabled={busy || loading} onClick={load}>
          Refresh
        </button>
      </div>

      {loading ? <p className="email-triggers-muted">Loading archived projects…</p> : null}
      {status ? (
        <p className="email-triggers-muted" role="status">
          {status}
        </p>
      ) : null}
      {error ? (
        <p className="email-triggers-muted" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && rows.length === 0 ? (
        <p className="email-triggers-muted">No archived projects.</p>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table className="email-triggers-table" style={{ width: "100%", minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    aria-label="Select all archived projects"
                    onChange={toggleAll}
                    disabled={busy}
                  />
                </th>
                <th>Work order</th>
                <th>Name</th>
                <th>Customer</th>
                <th>Archived</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      aria-label={`Select archive ${row.work_order || row.name || row.id}`}
                      onChange={() => toggleOne(row.id)}
                      disabled={busy}
                    />
                  </td>
                  <td>{row.work_order || "—"}</td>
                  <td>{row.name || "—"}</td>
                  <td>{row.customer || "—"}</td>
                  <td>{formatWhen(row.archived_at)}</td>
                  <td>{row.archived_by_email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
